/**
 * User avatar helpers: sanitized profile colors from the API plus deterministic fallbacks.
 * Backend can store `#RRGGBB` on the user record; optional `profilePictureUrl` may point to
 * a presigned HTTPS URL — the frontend only uses `<img [src]>`, so transport security is HTTPS.
 */

const SAFE_HEX_PATTERN = /^#([0-9a-f]{6})$/i;

/** Returns lowercase `#RRGGBB`, or null if the value is not a safe 6‑digit hex. */
export function parseSafeProfileAvatarHex(color: string | null | undefined): string | null {
  const t = typeof color === 'string' ? color.trim() : '';
  if (!t) return null;
  const withHash = t.startsWith('#') ? t : `#${t}`;
  const m = withHash.match(SAFE_HEX_PATTERN);
  return m ? `#${m[1].toLowerCase()}` : null;
}

const FALLBACK_AVATAR_COLORS = [
  '#5568d3',
  '#2d9264',
  '#c2410c',
  '#7c3aed',
  '#0d9488',
  '#b45309',
  '#0369a1',
  '#9333ea',
  '#047857',
  '#be123c',
] as const;

function hashString(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (Math.imul(31, h) + seed.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/** Stable opaque `#RRGGBB` per user id when API does not provide `profileAvatarHexColor`. */
export function defaultAvatarFallbackHex(seed: string): string {
  if (!seed) return FALLBACK_AVATAR_COLORS[0];
  return FALLBACK_AVATAR_COLORS[hashString(seed) % FALLBACK_AVATAR_COLORS.length];
}

/** Resolved opaque background color for initials (profile hex or deterministic fallback). */
export function resolvedAvatarBackgroundHex(user: {
  profileAvatarHexColor?: string | null | undefined;
  userID: string;
}): string {
  return parseSafeProfileAvatarHex(user.profileAvatarHexColor ?? undefined) ?? defaultAvatarFallbackHex(user.userID);
}

/**
 * Returns `#FFFFFF` or `#1F2937` for readable initials contrast on opaque backgrounds (WCAG heuristic).
 */
export function initialsTextColorHex(backgroundHex: string): string {
  const normalized = parseSafeProfileAvatarHex(backgroundHex) ?? defaultAvatarFallbackHex('fallback');
  const raw = normalized.slice(1);
  const r = parseInt(raw.slice(0, 2), 16) / 255;
  const g = parseInt(raw.slice(2, 4), 16) / 255;
  const b = parseInt(raw.slice(4, 6), 16) / 255;
  const l = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return l > 0.55 ? '#1f2937' : '#ffffff';
}
